import { Button, FormControl, FormLabel, Switch, useDisclosure, useToast } from '@chakra-ui/react'
import type { AxiosError } from 'axios'
import SubmitDialog from 'BrokerTsLib/src/components/SubmitDialog'
import type { ModelState, WizardTravelAdditionalData } from 'BrokerTsLib/src/dtos'
import { BadRequestError, InitialPersonAdditionalData } from 'BrokerTsLib/src/dtos'
import { Selection } from 'brokertslib/src/enums'
import { FormProvider, useForm } from 'BrokerTsLib/src/rhf'
import { setModelStateErrors } from 'BrokerTsLib/src/Utils/formModelState'
import { useEffect, useState } from 'react'
import { nameof } from 'ts-simple-nameof'

import { isIn } from 'BrokerTsLib/src/Utils/comparisonUtils'
import ErrorSummary from '../../components/ErrorSummary'
import {
  FormSection,
  FormSectionBody,
  FormSectionDescription,
  FormSectionHeader,
  FormSectionTitle,
} from '../../components/FormSection'
import { FormSpacer } from '../../components/FormSpacer'
import { Loading } from '../../components/Loading'
import { MainContent } from '../../components/MainContent'
import { SwitchControl } from '../../components/SwitchControl'
import { useGetStep4 } from '../../hooks/useWizard'
import { Save } from '../../icons'
import { useInsuranceParamsContext } from '../../layouts/Wizard'
import { AdditionalInsureeCestovkoInfo } from '../AdditionalInsureeCestovkoInfo'
import { FormButtons } from '../FormButtons'
import { AdditionalInsurees } from './AdditionalInsurees'
import { AdditionalVehicleAssistanceInfo } from './AdditionalVehicleAssistanceInfo'

interface Step4FormProps {
  onSubmit: (data: WizardTravelAdditionalData) => Promise<any>
}

export const Step4Form = ({ onSubmit }: Step4FormProps) => {
  const { insuranceParamsId, insuranceParams } = useInsuranceParamsContext()
  const isContract = !!insuranceParams?.generalParams.contractNumber
  const { isLoading, isFetching, data: axiosResponse, refetch } = useGetStep4(insuranceParamsId!, {
    enabled: false,
    cacheTime: 0,
    onSuccess: (values) => {
      reset(values.data)
    }
  })
  const data = axiosResponse?.data as WizardTravelAdditionalData

  useEffect(() => {
    refetch()
  }, [])

  const {
    isOpen: isSavedFormAlertOpen,
    onOpen: savedFormAlertOnOpen,
    onClose: savedFormAlertOnClose,
  } = useDisclosure()

  const methods = useForm<WizardTravelAdditionalData>({
    defaultValues: {
      policyHolder: InitialPersonAdditionalData,
      // ...data
    }
  })

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting, errors },
    setError,
    watch,
    clearErrors,
    setValue } = methods

  const toast = useToast()

  const submitForm = handleSubmit(values => {
    return onSubmit(values)
      .then(() => {
        if (values.nextPage == 'current') {
          savedFormAlertOnOpen()
          refetch()
        }
      })
      .catch((e: AxiosError) => {
        if (e.response?.status === 400) {
          setModelStateErrors(setError, new BadRequestError(e.response.data as ModelState<WizardTravelAdditionalData>))
        }
        else
          toast({
            title: 'Chyba při ukládání',
            status: 'error',
          })
      })
  })

  const [isSubmittingCurrent, setIsSubmittingCurrent] = useState(false)
  const [isSubmittingNext, setIsSubmittingNext] = useState(false)

  useEffect(() => {
    if (!isSubmitting) {
      setIsSubmittingCurrent(false)
      setIsSubmittingNext(false)
    }
  }, [isSubmitting])

  const phType = watch('policyHolder.type')
  const insurees = watch('insurees') ?? []

  return (
    <FormProvider {...methods}>
      <form onSubmit={submitForm}>
        <MainContent>
          <FormSection>
            <FormSectionHeader>
              <FormSectionTitle>Doplňující informace o&nbsp;pojistníkovi</FormSectionTitle>

              <FormSectionDescription>
                Doplňte informace o pojistníkovi a dalších osobách pro sjednání pojištění.
              </FormSectionDescription>
            </FormSectionHeader>

            {!data?.policyHolder && (isLoading || isFetching) && <Loading large={true} />}
            {data?.policyHolder &&
              <FormSectionBody>
                <AdditionalInsureeCestovkoInfo />

                {insurees && isIn(phType, Selection.Person, Selection.SelfEmployed) &&
                  <>
                    <FormControl>
                      <SwitchControl>
                        <Switch
                          checked={insurees.findIndex(x => x.isPolicyHolder) >= 0}
                          defaultChecked={insurees.findIndex(x => x.isPolicyHolder) >= 0}
                          isDisabled={true}
                        />

                        <FormLabel variant="switch">
                          Je zároveň pojištěnou osobou
                        </FormLabel>
                      </SwitchControl>
                    </FormControl>

                    <FormSpacer />
                  </>
                }

              </FormSectionBody>
            }
          </FormSection>

          <FormSection>
            <FormSectionHeader>
              <FormSectionTitle>Pojištěné osoby</FormSectionTitle>

              <FormSectionDescription>Doplňte informace o dalších osobách.</FormSectionDescription>
            </FormSectionHeader>

            <FormSectionBody>
              <AdditionalInsurees prefix={nameof<WizardTravelAdditionalData>(x => x.insurees)} editableBirthnumber />
            </FormSectionBody>
          </FormSection>

          {data?.vehicleData?.inputRequired &&
            <FormSection>
              <FormSectionHeader>
                <FormSectionTitle>Vozidlo</FormSectionTitle>

                <FormSectionDescription>Doplňte informace o vozidle pro připojištění asistence.</FormSectionDescription>
              </FormSectionHeader>

              <FormSectionBody>
                <AdditionalVehicleAssistanceInfo />
              </FormSectionBody>
            </FormSection>
          }

          <ErrorSummary errors={errors} />

          {!isContract &&
            <FormButtons
              rightButtons={
                <>
                  <Button
                    variant="outline"
                    disabled={isSubmitting || isSubmittingNext || isSubmittingCurrent}
                    isLoading={isSubmittingCurrent}
                    type="button"
                    onClick={() => {
                      clearErrors('insurees')
                      setValue('nextPage', 'current')
                      setIsSubmittingCurrent(true)
                      submitForm()
                    }}>
                    Uložit
                  </Button>
                  <Button
                    colorScheme="primary"
                    disabled={isSubmitting || isSubmittingNext || isSubmittingCurrent}
                    isLoading={isSubmittingNext}
                    loadingText="Ukládám..."
                    type="button"
                    onClick={() => {
                      clearErrors('insurees')
                      setValue('nextPage', 'next')
                      setIsSubmittingNext(true)
                      submitForm()
                    }}
                  >
                    <Save />
                    Uložit a pokračovat na shrnutí
                  </Button>

                  <SubmitDialog
                    isOpen={isSavedFormAlertOpen}
                    onClose={savedFormAlertOnClose}
                    header='Uloženo'
                    body={'Údaje byly úspěšně uloženy'}
                  />
                </>
              }
            />
          }
        </MainContent>
      </form>
    </FormProvider>
  )
}
